import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import {Box, Chip, createFilterOptions, Divider, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import {OpenInNew} from "@mui/icons-material";
import {Link} from "react-router-dom";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import Api from "../../../../core/services/api/api";
import {CheckboxForm} from "../../base/checkbox/checkbox";
import usePartnerConfiguration from "../../../hooks/use-partner-configuration";
import PhoneInput from "../../base/input/phone-input";
import ApiResponseCodes from "../../../../core/models/static/api-response-codes";
import {AddressTaxesDataGridCellKeys} from "../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridDensities} from "../../../../packages/data-grid";
import PartnerDataGrid, {useDataGridSavedState} from "../../../containers/data-grid";
import useCachedPriceLevelsDictionaryController from "../../../hooks/use-price-level-dictionary";
import ToastService from "../../../../core/services/toast";
import useAccessLevels from "../../../hooks/use-access-levels";
import {MuiRFF, Yup} from '../../form';
import CustomerAutocomplete from "../../text-fields/autocomplete/customer-autocomplete";
import EnvService from "../../../../core/services/env";
import {CustomerMemberShipNames} from "../../../../core/constants/enums";
import {formatPhoneNumber} from "../../../../core/services/utils/utils";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {routeFunctions} from "../../../../core/models/static/routes/app-routes";


const commonSchema = {
    firstName: Yup.string().trim().nullable().required(ValidateMessages.required),
    lastName: Yup.string().trim().nullable().required(ValidateMessages.required),
    email: Yup.string().email(ValidateMessages.incorrect).nullable().required(ValidateMessages.required),
    phone: Yup.string().trim().nullable()
        .required(ValidateMessages.required)
        .phoneNo(ValidateMessages.incorrectType("phone"), 10),
    company: Yup.mixed().nullable().required(ValidateMessages.required),
    sendEmail: Yup.boolean().nullable().required(ValidateMessages.required),
}

const companySpecificSchema = {
    companyPriceLevel: Yup.object().nullable().required(ValidateMessages.required),
    companyPaymentTerm: Yup.object().nullable().required(ValidateMessages.required),
    companyCountry: Yup.object().nullable().required(ValidateMessages.required),
    companyProvince: Yup.object().nullable().required(ValidateMessages.required),
    companyCity: Yup.string().trim().nullable().required(ValidateMessages.required),
    companyPhone: Yup.string().trim().nullable()
        .required(ValidateMessages.required)
        .phoneNo(ValidateMessages.incorrectType("phone"), 10),
    companyPostalCode: Yup.string().trim().nullable().required(ValidateMessages.required),
    companyAddress: Yup.string().trim().nullable().required(ValidateMessages.required),
}

const initialSchema = Yup.object().shape(commonSchema);

const withCompanyRegistrationSchema = (taxes) => Yup.object().shape({
    ...commonSchema,
    ...companySpecificSchema,
    ...taxes,
})

const taxIdentifier = 'province-tax-';
const DIALOG_VIEWS = {
    form: 'form',
    conflict: 'conflict',
};

/**
 * Strips non-digit characters for phone comparisons.
 * @param {string | null | undefined} value
 * @returns {string}
 */
const digitsOnly = (value) => (value ?? '').toString().replace(/\D/g, '');

/**
 * Builds a membership title lookup from server memberShips.
 * @param {Array<{name: string, title?: string}>} memberShips
 * @returns {Record<string, string>}
 */
const buildMembershipTitleMap = (memberShips) =>
    (memberShips ?? []).reduce((acc, membership) => {
        if (membership?.name) {
            acc[membership.name] = membership.title ?? membership.name;
        }
        return acc;
    }, {});

const CreateUserOnlineProfileDialog = ({
                                           open,
                                           setOpen,
                                           initialValues: initialValuesProp,
                                           onSuccess,
                                           onBack,
                                           canChangeCompanyInfo = false,
                                       }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.create-user-online-profile'});
    const dispatch = useDispatch();
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const [inTaxes, setInTaxes] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [countries, setCountries] = useState([]);
    const [provinces, setProvinces] = useState([]);
    const [priceLevels, setPriceLevels] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const [schema, setSchema] = useState(initialSchema);
    const [initialValues, setInitialValues] = useState({});
    const [registering, setRegistering] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [dialogView, setDialogView] = useState(DIALOG_VIEWS.form);
    const [conflictMatches, setConflictMatches] = useState([]);
    const [chosenCompany, setChosenCompany] = useState(null);
    const [memberShips, setMemberShips] = useState([]);
    const [actionLoadingUserId, setActionLoadingUserId] = useState(null);
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const {getValue} = usePartnerConfiguration();

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);
    const membershipTitles = useMemo(() => buildMembershipTitleMap(memberShips), [memberShips]);
    const isConflictView = dialogView === DIALOG_VIEWS.conflict;

    /**
     * Sets the initial Exemption numbers for the taxes of all the provinces.
     * @type {function(*=): void}
     */
    const setInitialValuesForForm = useCallback((provinces) => {
        const taxes = provinces?.map(province => province.taxes)?.reduce((prev, current) => prev && current ? [...prev, ...current] : []) ?? [];
        const res = {};
        taxes.forEach(tax => {
            res[`${taxIdentifier}${tax.id}`] = '';
        });
        setInitialValues({
            ...(initialValuesProp ?? []),
            sendEmail: getValue((ids) => ids.sendEmailUponCustomerRegistration) ?? false
        });
    }, [getValue, initialValuesProp])

    /**
     * Loads membership types used for conflict-user lookups across all statuses.
     * @type {function(): Promise<Array<{name: string, title?: string}>>}
     */
    const ensureMemberShips = useCallback(async () => {
        if (memberShips?.length) {
            return memberShips;
        }
        const response = await Api.getUserSearchForm();
        if (!isMounted()) {
            return [];
        }
        const next = response?.resultFlag ? (response.data?.memberShips ?? []) : [];
        setMemberShips(next);
        return next;
    }, [isMounted, memberShips])

    /**
     * gets the form data necessary for registration of the users.
     * The form info includes, countries, provinces, price levels, payment terms,
     * @type {function(): Promise<void>}
     */
    const getRegistrationForm = useCallback(async () => {
        const [response, priceLevelDict] = await Promise.all([
            Api.getUserProfileForm(),
            getPriceLevelDictionary(),
            ensureMemberShips(),
        ]);
        if (!isMounted())
            return;
        setDisabled(response?.errorCode === ApiResponseCodes.registrationDisabled);
        if (response?.resultFlag) {
            setCountries(response?.data.countries);
            const provinces = response.data.provinces?.map(e => ({
                ...e,
                taxes: response.data.provincesTaxes?.filter(t => t.provinceId === e.id)?.map(e => ({
                    id: e.id,
                    taxByProvince: e,
                    exemptNumber: ''
                })) ?? [],
            }));
            setProvinces(provinces);
            setPriceLevels(response.data.priceLevels.map(e => ({
                ...e,
                displayTitle: priceLevelDict?.find(dictionaryItem => dictionaryItem.priceLevelId === e.id)?.title ?? e.title ?? "",
            })));
            setPaymentTerms(response.data.paymentTerms);
            setInitialValuesForForm(provinces);
        }
    }, [ensureMemberShips, getPriceLevelDictionary, isMounted, setInitialValuesForForm])

    /**
     * Listens for when the modal opens and closes, and if open, gets the lists of the form data.
     */
    useEffect(() => {
        if (!open) {
            setInTaxes(false);
            setDialogView(DIALOG_VIEWS.form);
            setConflictMatches([]);
            setChosenCompany(null);
            setActionLoadingUserId(null);
            setRegistering(false);
        } else {
            if (!operatorAccessLevels.packages.base.createCustomerMember) {
                setOpen(false);
                ToastService.error({
                    title: t('accessDeniedTitle'),
                    message: t('accessDeniedMessage'),
                });
                return;
            }
            void getRegistrationForm();
        }
    }, [getRegistrationForm, open, operatorAccessLevels.packages.base.createCustomerMember, setOpen, t])

    /**
     * Looks up existing users by exact email and phone across all membership types.
     * @type {function(string, string): Promise<Array<{user: any, matchedBy: string[]}>>}
     */
    const lookupExistingUsers = useCallback(async (email, phone) => {
        const ships = await ensureMemberShips();
        const membershipNames = (ships ?? []).map((m) => m.name).filter(Boolean);
        const paginationInfo = {pageSize: 25, currentPage: 1, length: 0};
        const baseFilters = {
            memberShipNames: membershipNames,
            isOrderBySubmitDateADescending: false,
        };
        const [emailResponse, phoneResponse] = await Promise.all([
            Api.searchCurrentUsers({
                paginationInfo,
                filters: {...baseFilters, keyword: email},
            }),
            Api.searchCurrentUsers({
                paginationInfo,
                filters: {...baseFilters, keyword: phone},
            }),
        ]);
        if (!isMounted()) {
            return [];
        }

        const emailNeedle = (email ?? '').trim().toLowerCase();
        const phoneNeedle = digitsOnly(phone);
        const byId = new Map();

        (emailResponse?.resultFlag ? (emailResponse.data?.resultList ?? []) : []).forEach((user) => {
            if ((user?.email ?? '').trim().toLowerCase() !== emailNeedle) {
                return;
            }
            byId.set(user.id, {user, matchedBy: ['email']});
        });

        (phoneResponse?.resultFlag ? (phoneResponse.data?.resultList ?? []) : []).forEach((user) => {
            if (digitsOnly(user?.phone) !== phoneNeedle) {
                return;
            }
            const existing = byId.get(user.id);
            if (existing) {
                if (!existing.matchedBy.includes('phone')) {
                    existing.matchedBy.push('phone');
                }
            } else {
                byId.set(user.id, {user, matchedBy: ['phone']});
            }
        });

        return Array.from(byId.values());
    }, [ensureMemberShips, isMounted])

    /**
     * Shows the original create-user error toast when duplicate lookup finds no matches.
     * @type {function(any): void}
     */
    const showCreateErrorToast = useCallback((response) => {
        ToastService.error({
            title: response?.headerTitle,
            message: response?.message,
        });
    }, [])

    /**
     * Completes a successful conflict action: toast, close dialog, notify parent.
     * @type {function(string): void}
     */
    const finishConflictAction = useCallback((successMessageKey) => {
        ToastService.success({
            title: t('conflict.successTitle'),
            message: t(successMessageKey),
        });
        onSuccess?.();
        setOpen(false);
    }, [onSuccess, setOpen, t])

    /**
     * Registers a new user into the system. The user can select from the already existing companies or register a
     * new company as well.
     *
     * if registering a new company:
     *  * if not yet in taxes pages => show the taxes pages.
     *  * else => register the user with their new company and the taxes exemption numbers for the company
     *
     * else => registers the user to an existing company.
     * @type {function(*): Promise<void>}
     */
    const registerUser = useCallback(async (values) => {
        const isRegisteringCompany = values.company?.id === -1;
        if (isRegisteringCompany && !inTaxes) {
            setInTaxes(true);
            return;
        }
        const forApi = {
            newUserDS: {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phone: values.phone,
            },
            dontNotifyUser: !values.sendEmail,
        };
        setChosenCompany(values.company ?? null);
        setRegistering(true);

        let response;
        if (!canChangeCompanyInfo && !isRegisteringCompany) {
            forApi['code'] = values.company?.code;
            response = await Api.fastCreateUserFromSpireCustomer(forApi, true);
        } else {
            if (isRegisteringCompany) {
                const taxIdentifiers = Object.keys(values).filter(e => e.match(RegExp(`^${taxIdentifier}\\S`))) ?? [];
                forApi['newCustomer'] = {
                    name: values.company?.name,
                    province: values.companyProvince,
                    city: values.companyCity,
                    postalCode: values.companyPostalCode,
                    phone: values.companyPhone,
                    address: values.companyAddress,
                    priceLevels: [{priceLevel: values.companyPriceLevel}],
                    paymentTerms: [values.companyPaymentTerm],
                    addressTaxes: taxIdentifiers.map(identifier => {
                        if (!values[identifier]?.length) return null;
                        const id = parseInt(identifier.replace(taxIdentifier, ''));
                        const tax = values.companyProvince?.taxes?.find(t => t.id === id);
                        return {
                            taxByProvince: tax.taxByProvince,
                            exemptNumber: values[identifier],
                        };
                    })?.filter(e => e),
                }
            } else {
                forApi['code'] = values.company?.code;
            }
            response = await Api.createUser(forApi, true);
        }

        if (!isMounted()) {
            return;
        }

        if (response?.resultFlag) {
            setRegistering(false);
            onSuccess?.();
            setOpen(false);
            return;
        }

        if (response?.errorCode === ApiResponseCodes.userAlreadyExists) {
            const matches = await lookupExistingUsers(values.email, values.phone);
            if (!isMounted()) {
                return;
            }
            setRegistering(false);
            if (!matches.length) {
                showCreateErrorToast(response);
                return;
            }
            // Keep submitted values so Back can restore the form for tweaks / resubmit.
            setInitialValues((prev) => ({...(prev ?? {}), ...values}));
            setConflictMatches(matches);
            setDialogView(DIALOG_VIEWS.conflict);
            return;
        }

        setRegistering(false);
        showCreateErrorToast(response);
    }, [canChangeCompanyInfo, inTaxes, isMounted, lookupExistingUsers, onSuccess, setOpen, showCreateErrorToast])

    /**
     * Removes all the company related information if the changed company is an already existing company.
     * @type {function(*=, *=, *=, *=): void}
     */
    const onCompanyChange = useCallback((_, newCompany, values, form) => {
        form.change('company', newCompany);
        if (newCompany?.id !== -1) {
            form.change('companyProvince', null);
            form.change('companyCountry', null);
            form.change('companyPhone', null);
            form.change('companyPostalCode', null);
            form.change('companyCity', null);
            form.change('companyAddress', null);
            form.change('companyPaymentTerm', null);
            form.change('companyPriceLevel', null);
            Object.keys(values).filter(e => e.match(RegExp(`^${taxIdentifier}\\S`)))?.forEach(e => {
                form.change(e, null);
            })
            setInTaxes(false);
            setSchema(initialSchema)
        } else {
            setSchema(withCompanyRegistrationSchema())
        }
    }, [])

    /**
     * Removes the value of province as the country of the company changes and also nullifies all the exempt number
     * inputs for that specific province.
     * @type {function(*=, *=, *=): void}
     */
    const onCountryChange = useCallback((_, values, form) => {
        form.change('companyProvince', null);
        Object.keys(values).filter(e => e.match(RegExp(`^${taxIdentifier}\\S`)))?.forEach(e => {
            form.change(e, null);
        })
    }, [])

    /**
     * Removes all the exempt number values from the form and then assigns the new tax inputs for the newly selected
     * province.
     * @type {function(*=, *=, *=): void}
     */
    const onProvinceChange = useCallback((e, values, form) => {
        Object.keys(values).filter(e => e.match(RegExp(`^${taxIdentifier}\\S`)))?.forEach(e => {
            form.change(e, null);
        })
        const newTaxes = {};
        e.target.value.taxes?.forEach(tax => {
            newTaxes[`${taxIdentifier}${tax.id}`] = Yup.string().nullable();
        });
        setSchema(withCompanyRegistrationSchema(newTaxes))
    }, [])

    /**
     * Goes back to the previous page of the form.
     * If in taxes page, goes back to the company registration page.
     * If in conflict view, returns to the form with preserved values.
     * @type {function(): void}
     */
    const goBack = useCallback(() => {
        if (isConflictView) {
            setDialogView(DIALOG_VIEWS.form);
            setConflictMatches([]);
            setActionLoadingUserId(null);
            return;
        }
        if (inTaxes) {
            setInTaxes(false);
            return;
        }
        if (onBack) {
            onBack();
        }
        setOpen(false);
    }, [inTaxes, isConflictView, onBack, setOpen])

    /**
     * Assigns the matched user to the chosen customer from the form.
     * @type {function(any): Promise<void>}
     */
    const assignToChosenCustomer = useCallback(async (user) => {
        if (!chosenCompany?.code || chosenCompany?.id === -1) {
            return;
        }
        setActionLoadingUserId(user.id);
        const response = await Api.AssignCompanyToUser({
            userId: user.id,
            code: chosenCompany.code,
            dontNotifyUser: false,
        });
        if (!isMounted()) {
            return;
        }
        setActionLoadingUserId(null);
        if (response?.resultFlag) {
            finishConflictAction('conflict.successAssign');
        }
    }, [chosenCompany, finishConflictAction, isMounted])

    /**
     * Detaches the matched user from their current customer.
     * @type {function(any): void}
     */
    const detachFromCustomer = useCallback((user) => {
        const memberName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: t('conflict.detachConfirmTitle'),
            removedEntryTitle: t('conflict.detachConfirmEntry', {name: memberName}),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return true;
                }
                setActionLoadingUserId(user.id);
                const response = await Api.removeCompanyFromUser(user.id);
                if (!isMounted()) {
                    return true;
                }
                setActionLoadingUserId(null);
                if (response?.resultFlag) {
                    finishConflictAction('conflict.successDetach');
                }
                return response?.resultFlag;
            },
        }));
    }, [dispatch, finishConflictAction, isMounted, t])

    /**
     * Detaches the matched user then reattaches them to the chosen customer.
     * @type {function(any): void}
     */
    const detachAndReattach = useCallback((user) => {
        if (!chosenCompany?.code || chosenCompany?.id === -1) {
            return;
        }
        const memberName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: t('conflict.detachConfirmTitle'),
            removedEntryTitle: t('conflict.detachConfirmEntry', {name: memberName}),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return true;
                }
                setActionLoadingUserId(user.id);
                const detachResponse = await Api.removeCompanyFromUser(user.id);
                if (!isMounted()) {
                    return true;
                }
                if (!detachResponse?.resultFlag) {
                    setActionLoadingUserId(null);
                    return false;
                }
                const assignResponse = await Api.AssignCompanyToUser({
                    userId: user.id,
                    code: chosenCompany.code,
                    dontNotifyUser: false,
                });
                if (!isMounted()) {
                    return true;
                }
                setActionLoadingUserId(null);
                if (assignResponse?.resultFlag) {
                    finishConflictAction('conflict.successDetachAndReattach');
                }
                return assignResponse?.resultFlag;
            },
        }));
    }, [chosenCompany, dispatch, finishConflictAction, isMounted, t])

    /**
     * Deactivates the matched user while retaining their assigned customer.
     * @type {function(any): void}
     */
    const deactivateUser = useCallback((user) => {
        const memberName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: t('conflict.deactivateConfirmTitle'),
            removedEntryTitle: t('conflict.detachConfirmEntry', {name: memberName}),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return true;
                }
                setActionLoadingUserId(user.id);
                const response = await Api.toggleUserStatus(user.id);
                if (!isMounted()) {
                    return true;
                }
                setActionLoadingUserId(null);
                if (response?.resultFlag) {
                    finishConflictAction('conflict.successDeactivate');
                }
                return response?.resultFlag;
            },
        }));
    }, [dispatch, finishConflictAction, isMounted, t])

    const filterCustomerOptionsAlgo = useMemo(() => createFilterOptions({
        ignoreCase: true,
        limit: 100,
        stringify: (option) => option?.name ?? ""
    }), []);

    /**
     * Renders the option for the company autocomplete.
     * @type {function(*, *): unknown[]}
     */
    const filterCustomerAutocompleteOptions = useCallback((options, params) => {
        const filtered = filterCustomerOptionsAlgo(options, params);
        if (
            !!params.inputValue?.trim()
            && !loadingCustomers
            && !filtered?.find(e => e?.name?.toLowerCase()?.trim() === params.inputValue.toLowerCase().trim())
            && operatorAccessLevels.packages.base.createCustomer
        ) {
            filtered.push({
                id: -1,
                name: params.inputValue,
            })
        }
        return filtered;
    }, [filterCustomerOptionsAlgo, loadingCustomers, operatorAccessLevels.packages.base.createCustomer])

    /**
     * Renders the option for the company autocomplete.
     * @type {function(*, *, *, *, *): *}
     */
    const renderCustomerOption = useCallback(({key, ...props}, option, state, defaultRenderer,) => {
        if (option?.id === -1) {
            return (
                <Box key={key} {...props} sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                    {t('createCustomerOption', {name: option?.name ?? ''})}
                </Box>
            )
        }
        return defaultRenderer(props, option, state)
    }, [t])

    /**
     * Resolves the conflict matched-by chip label.
     * @type {function(string[]): string}
     */
    const matchedByLabel = useCallback((matchedBy) => {
        const hasEmail = matchedBy?.includes('email');
        const hasPhone = matchedBy?.includes('phone');
        if (hasEmail && hasPhone) {
            return t('conflict.matchedByBoth');
        }
        if (hasEmail) {
            return t('conflict.matchedByEmail');
        }
        return t('conflict.matchedByPhone');
    }, [t])

    /**
     * Whether the chosen company can be used for assign / reattach actions.
     */
    const canUseChosenCustomer = !!chosenCompany?.code && chosenCompany?.id !== -1;

    const dialogTitle = useMemo(() => {
        if (isConflictView) {
            return t('titles.userAlreadyExists');
        }
        if (initialValuesProp?.company) {
            return t('titles.onlineProfile');
        }
        if (inTaxes) {
            return t('titles.taxInformation');
        }
        return t('titles.addUser');
    }, [inTaxes, initialValuesProp?.company, isConflictView, t])

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            className={'add-new-user-dialog'}
            maxWidth={'sm'}
            loading={registering || !!actionLoadingUserId}
            disableBackdropClick={registering || !!actionLoadingUserId}
            disableEscapeKeyDown={registering || !!actionLoadingUserId}
        >
            <Row className='no-gutters align-items-center w-100 transition'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className='title'>
                        {dialogTitle}
                    </p>
                </Row>
                <Divider className='divider'/>
                <div className='my-3 w-100'/>
                {
                    isConflictView &&
                    <ExistingUserConflictView
                        matches={conflictMatches}
                        membershipTitles={membershipTitles}
                        chosenCompany={chosenCompany}
                        canUseChosenCustomer={canUseChosenCustomer}
                        canOpenProfile={!!operatorAccessLevels.packages.base.customerDetails}
                        actionLoadingUserId={actionLoadingUserId}
                        matchedByLabel={matchedByLabel}
                        onBack={goBack}
                        onAssign={assignToChosenCustomer}
                        onDetach={detachFromCustomer}
                        onDetachAndReattach={detachAndReattach}
                        onDeactivate={deactivateUser}
                        t={t}
                    />
                }
                {
                    !isConflictView &&
                    <>
                        {
                            !!initialValuesProp?.company &&
                            <Row className={'no-gutters px-3 pb-4 w-100'}>
                                <p className={'description'}>
                                    {t('onlineProfileDescription')}
                                </p>
                            </Row>
                        }
                        <Form
                            className='w-100'
                            onSubmit={registerUser}
                            validate={validate}
                            initialValues={initialValues}
                            render={({submitting, values, initialValues, form}) => {
                                const isRegisteringCompany = values.company?.id === -1;
                                const colClass = 'px-3 mb-4';
                                return (
                                    <Row className='no-gutters justify-content-around w-100'>
                                        {
                                            inTaxes && isRegisteringCompany &&
                                            <Row className='no-gutters w-100 justify-content-center'>
                                                <CompanyTaxes
                                                    province={values.companyProvince}
                                                    form={form}
                                                    values={values}
                                                    initialValues={initialValues}
                                                    required={required}
                                                    t={t}
                                                />
                                            </Row>
                                        }
                                        {
                                            !(inTaxes && isRegisteringCompany) &&
                                            <Row className='no-gutters justify-content-around w-100'>
                                                <Col xs={6} className={colClass}>
                                                    <OutlinedInputForm
                                                        label={t('fields.firstName')}
                                                        aria-label={t('fields.firstName')}
                                                        name={'firstName'}
                                                        type={'text'}
                                                        required={required.firstName}
                                                        value={values?.firstName ?? ''}
                                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                                    />
                                                </Col>
                                                <Col xs={6} className={colClass}>
                                                    <OutlinedInputForm
                                                        label={t('fields.lastName')}
                                                        name={'lastName'}
                                                        type={'text'}
                                                        required={required.lastName}
                                                        value={values?.lastName ?? ''}
                                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                                    />
                                                </Col>
                                                <Col xs={12} className={colClass}>
                                                    <OutlinedInputForm
                                                        label={t('fields.email')}
                                                        name={'email'}
                                                        type={'text'}
                                                        required={required.email}
                                                        value={values?.email ?? ''}
                                                    />
                                                </Col>
                                                <Col xs={12} className={colClass}>
                                                    <PhoneInput
                                                        form={form}
                                                        label={t('fields.phone')}
                                                        name={'phone'}
                                                        required={required.phone}
                                                        value={values?.phone ?? ''}
                                                    />
                                                </Col>
                                                <Col xs={12} className={colClass}>
                                                    <CustomerAutocomplete
                                                        form
                                                        disabled={!canChangeCompanyInfo}
                                                        onChange={(e, val) => onCompanyChange(e, val, values, form)}
                                                        label={t('fields.customer')}
                                                        placeholder={t('fields.customerPlaceholder')}
                                                        name={'company'}
                                                        required={required.company}
                                                        filterOptions={filterCustomerAutocompleteOptions}
                                                        renderOption={renderCustomerOption}
                                                        setLoadingOptionsProps={setLoadingCustomers}
                                                    />
                                                </Col>
                                            </Row>
                                        }
                                        {
                                            !inTaxes && isRegisteringCompany &&
                                            <CompanyRegistration
                                                form={form}
                                                values={values}
                                                initialValues={initialValues}
                                                provinces={provinces}
                                                countries={countries}
                                                priceLevels={priceLevels}
                                                paymentTerms={paymentTerms}
                                                required={required}
                                                onCountryChange={onCountryChange}
                                                onProvinceChange={onProvinceChange}
                                                t={t}
                                            />
                                        }
                                        <Row className={'no-gutters w-100 justify-content-start px-3'}>
                                            <CheckboxForm
                                                name={'sendEmail'}
                                                required={required.sendEmail}
                                                data={{
                                                    value: values.sendEmail, label: t('fields.sendEmail')
                                                }}
                                            />
                                        </Row>
                                        <div className='my-1 w-100'/>
                                        <Divider className='divider'/>
                                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                                            <Button
                                                disabled={!open || submitting || registering}
                                                variant={'dialog'}
                                                color={'primary'}
                                                onClick={() => goBack()}
                                                borderRadius={'0 8px 0 8px'}
                                            >
                                                {inTaxes ? t('actions.back') : t('actions.close')}
                                            </Button>
                                            <Button
                                                disabled={!open || submitting || registering || disabled}
                                                variant={'dialog'}
                                                color={'primary'}
                                                type={'submit'}
                                                borderRadius={'8px 0 8px 0'}
                                            >
                                                {
                                                    inTaxes
                                                        ? t('actions.register')
                                                        : isRegisteringCompany
                                                            ? t('actions.next')
                                                            : registering || submitting
                                                                ? t('actions.registering')
                                                                : t('actions.register')
                                                }
                                            </Button>
                                        </Row>
                                    </Row>
                                );
                            }}
                        />
                    </>
                }
            </Row>
        </Dialog>
    );
}

/**
 * Conflict recovery view shown when create returns errorCode 500 and matching users are found.
 */
const ExistingUserConflictView = ({
                                      matches,
                                      membershipTitles,
                                      chosenCompany,
                                      canUseChosenCustomer,
                                      canOpenProfile,
                                      actionLoadingUserId,
                                      matchedByLabel,
                                      onBack,
                                      onAssign,
                                      onDetach,
                                      onDetachAndReattach,
                                      onDeactivate,
                                      t,
                                  }) => {
    const chosenCustomerLabel = (() => {
        if (canUseChosenCustomer) {
            return t('conflict.chosenCustomerValue', {
                name: chosenCompany?.name ?? '',
                code: chosenCompany?.code ?? '',
            });
        }
        if (chosenCompany?.name) {
            return t('conflict.chosenCustomerNew', {
                name: chosenCompany.name,
            });
        }
        return t('conflict.chosenCustomerMissing');
    })();

    return (
        <Box sx={{width: '100%', px: 3, pb: 3}}>
            <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
                {t('conflict.description')}
            </Typography>
            <Box
                sx={{
                    border: 1,
                    borderColor: 'primary.light',
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    width: '100%',
                }}
            >
                <Typography variant="subtitle2" fontWeight={600} sx={{mb: 0.5}}>
                    {t('conflict.chosenCustomer')}
                </Typography>
                <Typography variant="body2">
                    {chosenCustomerLabel}
                </Typography>
            </Box>
            <Stack spacing={2} sx={{width: '100%', mb: 3}}>
                {matches.map(({user, matchedBy}) => {
                    const hasCustomer = !!user?.customer?.id || !!user?.customer?.code;
                    const sameCustomer = hasCustomer && (
                        (chosenCompany?.id && user.customer?.id === chosenCompany.id)
                        || (chosenCompany?.code && user.customer?.code === chosenCompany.code)
                    );
                    const isOnHoldOrWithoutCustomer =
                        user?.memberShipName === CustomerMemberShipNames.onHold
                        || user?.memberShipName === CustomerMemberShipNames.onHoldCompanyLost
                        || !hasCustomer;
                    const isActive = user?.memberShipName === CustomerMemberShipNames.active;
                    const loading = actionLoadingUserId === user.id;
                    const statusTitle = membershipTitles[user?.memberShipName] ?? user?.memberShipName ?? '';
                    const showOpenProfile = canOpenProfile && !!user?.customer?.id;

                    return (
                        <Box
                            key={user.id}
                            sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 2,
                                width: '100%',
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{mb: 1.5}}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{flexShrink: 0}}>
                                    <Chip size="small" label={matchedByLabel(matchedBy)} color="primary" variant="outlined"/>
                                    {
                                        showOpenProfile &&
                                        <Tooltip title={t('conflict.openProfile')}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    component={Link}
                                                    to={routeFunctions.customer.generalInformation.profile(user.customer.id)}
                                                    aria-label={t('conflict.openProfile')}
                                                    disabled={loading || !!actionLoadingUserId}
                                                >
                                                    <OpenInNew fontSize="small"/>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    }
                                </Stack>
                            </Stack>
                            <Stack spacing={0.75} sx={{mb: 2}}>
                                <Typography variant="body2"><strong>{t('conflict.email')}:</strong> {user.email ?? ''}</Typography>
                                <Typography variant="body2"><strong>{t('conflict.phone')}:</strong> {formatPhoneNumber(user.phone) ?? user.phone ?? ''}</Typography>
                                <Typography variant="body2"><strong>{t('conflict.status')}:</strong> {statusTitle}</Typography>
                                <Typography variant="body2">
                                    <strong>{t('conflict.assignedCustomer')}:</strong>{' '}
                                    {hasCustomer
                                        ? `${user.customer?.name ?? ''}${user.customer?.code ? ` (${user.customer.code})` : ''}`
                                        : t('conflict.noCustomer')}
                                </Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                useFlexGap
                                spacing={1}
                                justifyContent="flex-end"
                                sx={{width: '100%'}}
                            >
                                {
                                    hasCustomer &&
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        disabled={loading || !!actionLoadingUserId}
                                        onClick={() => onDetach(user)}
                                    >
                                        {loading ? t('conflict.processing') : t('conflict.detach')}
                                    </Button>
                                }
                                {
                                    isActive &&
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        disabled={loading || !!actionLoadingUserId}
                                        onClick={() => onDeactivate(user)}
                                    >
                                        {loading ? t('conflict.processing') : t('conflict.deactivate')}
                                    </Button>
                                }
                                {
                                    canUseChosenCustomer && hasCustomer && !sameCustomer &&
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={loading || !!actionLoadingUserId}
                                        onClick={() => onDetachAndReattach(user)}
                                    >
                                        {loading ? t('conflict.processing') : t('conflict.detachAndReattach')}
                                    </Button>
                                }
                                {
                                    canUseChosenCustomer && isOnHoldOrWithoutCustomer &&
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={loading || !!actionLoadingUserId}
                                        onClick={() => onAssign(user)}
                                    >
                                        {loading ? t('conflict.processing') : t('conflict.assign')}
                                    </Button>
                                }
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
            <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={!!actionLoadingUserId}
                    onClick={onBack}
                >
                    {t('actions.back')}
                </Button>
            </Box>
        </Box>
    );
}

const CompanyRegistration = ({
                                 required,
                                 priceLevels,
                                 paymentTerms,
                                 countries,
                                 values,
                                 provinces,
                                 form,
                                 onProvinceChange,
                                 onCountryChange,
                                 t,
                             }) => {
    const colClass = 'px-3 mb-4';
    const provinceLabel = !values.companyCountry
        ? t('fields.provinceState')
        : values.companyCountry?.name === EnvService.USATitle
            ? t('fields.state')
            : t('fields.province');
    const postalLabel = !values.companyCountry
        ? t('fields.postalZipCode')
        : values.companyCountry?.name === EnvService.USATitle
            ? t('fields.zipCode')
            : t('fields.postalCode');

    return (
        <Row className='no-gutters justify-content-center w-100'>
            <Col xs={6} className={colClass}>
                <OutlinedDropdownForm
                    label={t('fields.priceLevel')}
                    name={`companyPriceLevel`}
                    required={required.companyPriceLevel}
                    data={priceLevels.map(e => ({
                        value: e,
                        label: e.displayTitle,
                        id: e.id
                    })) ?? []}
                />
            </Col>
            <Col xs={6} className={colClass}>
                <OutlinedDropdownForm
                    label={t('fields.paymentTerms')}
                    name={`companyPaymentTerm`}
                    required={required.companyPaymentTerm}
                    data={paymentTerms.map(e => ({
                        value: e,
                        label: e.description,
                        id: e.id
                    })) ?? []}
                />
            </Col>
            <Col xs={6} className={colClass}>
                <OutlinedDropdownForm
                    label={t('fields.country')}
                    onChange={(e) => onCountryChange(e, values, form)}
                    name={`companyCountry`}
                    required={required.companyCountry}
                    data={countries.map(e => ({
                        value: e,
                        label: e.name,
                        id: e.id
                    })) ?? []}
                />
            </Col>
            <Col xs={6} className={colClass}>
                <OutlinedDropdownForm
                    label={provinceLabel}
                    onChange={(e) => onProvinceChange(e, values, form)}
                    name={`companyProvince`}
                    required={required.companyProvince}
                    data={provinces?.filter(e => e.countryId === values.companyCountry?.id)?.map(e => ({
                        value: e,
                        label: e.name,
                        id: e.id
                    })) ?? []}
                />
            </Col>
            <Col xs={6} className={colClass}>
                <PhoneInput
                    form={form}
                    label={t('fields.customerPhone')}
                    name={'companyPhone'}
                    required={required.companyPhone}
                    value={values?.companyPhone ?? ''}
                />
            </Col>
            <Col xs={6} className={colClass}>
                <OutlinedInputForm
                    label={t('fields.city')}
                    name={'companyCity'}
                    required={required.companyCity}
                    value={values?.companyCity ?? ''}
                    onChange={(e) => form.change(e.target.name, e.target.value)}
                />
            </Col>
            <Col xs={8} className={colClass}>
                <OutlinedInputForm
                    label={t('fields.address')}
                    name={'companyAddress'}
                    type={'text'}
                    required={required.companyAddress}
                    value={values?.companyAddress ?? ''}
                    onChange={(e) => form.change(e.target.name, e.target.value)}
                />
            </Col>
            <Col xs={4} className={colClass}>
                <OutlinedInputForm
                    label={postalLabel}
                    name={'companyPostalCode'}
                    required={required.companyPostalCode}
                    value={values?.companyPostalCode ?? ''}
                    onChange={(e) => form.change(e.target.name, e.target.value)}
                />
            </Col>
        </Row>
    );
}

const CompanyTaxes = ({
                          province,
                          values,
                          form,
                          required,
                          t,
                      }) => {

    const dataGridSaveConfig = useMemo(() => ({
        name: 'CreateUserOnlineProfileCompanyTaxesSectionDataGrid',
        version: 1,
    }), [])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: t('taxes.tax'),
                name: AddressTaxesDataGridCellKeys.tax,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                width: 30,
            },
            {
                title: t('taxes.rate'),
                name: AddressTaxesDataGridCellKeys.rate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.number,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                width: 40,
            },
            {
                title: t('taxes.exemptNo'),
                name: AddressTaxesDataGridCellKeys.exemptNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                width: 70,
            },
        ], [t])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return province?.taxes?.map(addressTax => ({
            key: addressTax.id,
            data: addressTax,
            cells: {
                [AddressTaxesDataGridCellKeys.tax]: addressTax?.taxByProvince?.title,
                [AddressTaxesDataGridCellKeys.rate]: `${addressTax?.taxByProvince?.percentage ?? 0}%`,
                [AddressTaxesDataGridCellKeys.exemptNo]:
                    <OutlinedInputForm
                        size={'small'}
                        name={`${taxIdentifier}${addressTax.id}`}
                        required={required[`${taxIdentifier}${addressTax.id}`]}
                        value={values[`${taxIdentifier}${addressTax.id}`] ?? ''}
                        onChange={(e) => form.change(e.target.name, e.target.value)}
                    />,
            },
        }))
    }, [form, province?.taxes, required, values])

    /**@type {JSX.Element}*/
    const emptyContent = useMemo(() => (
        <div>
            <p className={'d-flex align-items-center justify-content-center text-center text-sm'}>
                {t('taxes.empty')}
            </p>
        </div>
    ), [t])

    return (
        <PartnerDataGrid
            {...dataGridSaveConfig}
            initialLayoutState={{
                columns: columns,
            }}
            columns={dataGridColumns}
            rows={dataGridRows}
            classNames={{
                layout: 'mt-0 mb-4',
            }}
            state={{
                pagination: {pageSize: province?.taxes?.length ?? 0},
                density: DataGridDensities.compact,
            }}
            EmptyContentComponent={emptyContent}
            hideToolbar
            hideFooter
        />
    );
}

export default CreateUserOnlineProfileDialog;
